import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LockResetIcon from '@mui/icons-material/LockReset';
import { useNavigate } from 'react-router-dom';
import { driverNeedsKnowledgeTraining, useAuth } from '../context/AuthContext';

const loginFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
  },
  '& .MuiInputBase-input': {
    color: '#1a1a1a',
    WebkitTextFillColor: '#1a1a1a',
    caretColor: '#1a1a1a',
    colorScheme: 'light',
    '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {
      WebkitBoxShadow: '0 0 0 100px #fff inset',
      boxShadow: '0 0 0 100px #fff inset',
      WebkitTextFillColor: '#1a1a1a',
      caretColor: '#1a1a1a',
      transition: 'background-color 9999s ease-out',
    },
  },
};

export default function Login() {
  const { login, resetPassword, currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Forgot / Reset Password Modal & Toast state
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetToastOpen, setResetToastOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Please enter both your email and password.');
      return;
    }

    setSubmitting(true);
    const result = await login(email, password);

    if (!result.success) {
      setSubmitting(false);
      setError(result.error || 'Login failed. Please verify your credentials.');
      return;
    }

    const destination =
      result.user?.role === 'admin' || result.user?.role === 'manager'
        ? '/admin'
        : driverNeedsKnowledgeTraining(result.user)
          ? '/training'
          : '/schedule';
    navigate(destination, { replace: true });
  };

  const openResetDialog = () => {
    setResetEmail(email.trim() || currentUser?.email || '');
    setResetError(null);
    setResetOpen(true);
  };

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError(null);

    if (!resetEmail.trim()) {
      setResetError('Please enter your email address.');
      return;
    }

    setResetSubmitting(true);
    const res = await resetPassword(resetEmail);
    setResetSubmitting(false);

    if (!res.success) {
      setResetError(res.error || 'Could not send password reset email.');
    } else {
      setResetOpen(false);
      setResetToastOpen(true);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: { xs: '84px', sm: '260px', md: '230px' },
        paddingBottom: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: 'linear-gradient(135deg, #0D47A1 0%, #1976d2 50%, transparent 50%)',
          opacity: 0.08,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(13, 71, 161, 0.15)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976d2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                mb: 2,
                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.4)',
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 32 }} />
            </Box>

            <Typography
              variant="h4"
              component="h1"
              align="center"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(45deg, #0D47A1 30%, #1976d2 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                mb: 0.5,
              }}
            >
              SDS Portal Login
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Success Delivery Services — Driver & Administrator Access
            </Typography>
          </Box>

          {currentUser && !submitting ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                You are currently signed in as <strong>{currentUser.name}</strong> ({currentUser.email}) with{' '}
                <strong>{currentUser.role.toUpperCase()}</strong> access.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<AdminPanelSettingsIcon />}
                    onClick={() => navigate('/admin')}
                  >
                    Go to {currentUser.role === 'manager' ? 'Manager' : 'Admin'} Portal
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => navigate('/schedule')}
                >
                  Go to My Schedule
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => navigate('/training')}
                >
                  Go to Driver Knowledge Base
                </Button>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<LockResetIcon />}
                  onClick={openResetDialog}
                >
                  Reset Password
                </Button>
                <Button
                  color="error"
                  onClick={async () => {
                    await logout();
                  }}
                >
                  Sign Out
                </Button>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert severity="error" sx={{ mb: 2.5 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                required
                label="SDS Email Address"
                type="email"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="driver@sds.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                sx={loginFieldSx}
              />

              <TextField
                fullWidth
                required
                label="Password"
                type={showPassword ? 'text' : 'password'}
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={loginFieldSx}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Button
                  size="small"
                  onClick={openResetDialog}
                  sx={{ fontSize: '0.85rem', py: 0.5, px: 1 }}
                >
                  Forgot Password?
                </Button>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #0D47A1 0%, #1976d2 100%)',
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>
          )}
        </Paper>

        {/* Forgot / Reset Password Modal */}
        <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
          <Box component="form" onSubmit={handleResetSubmit}>
            <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockResetIcon color="primary" /> Reset Your Password
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your SDS email address below and we will send you a password reset link via Firebase.
              </Typography>

              {resetError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {resetError}
                </Alert>
              )}

              <TextField
                fullWidth
                required
                size="small"
                type="email"
                label="SDS Email Address"
                placeholder="driver@sds.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon color="primary" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={loginFieldSx}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setResetOpen(false)}>Close</Button>
              <Button type="submit" variant="contained" disabled={resetSubmitting}>
                {resetSubmitting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        {/* Password Reset Toast Notification */}
        <Snackbar
          open={resetToastOpen}
          autoHideDuration={5000}
          onClose={() => setResetToastOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setResetToastOpen(false)}
            severity="success"
            variant="filled"
            sx={{ width: '100%', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}
          >
            A password reset link has been emailed to you.
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
